<template>
    <div>
      <apexchart
        ref="apex"
        type="bar"
        :options="chartOptions"
        :series="localSeries"
        :height="height"
      />
    </div>
  </template>
  
  <script>
  import VueApexCharts from 'vue-apexcharts'
  
  export default {
    name: 'StackedBarChart',
    components: {
      apexchart: VueApexCharts
    },
    props: {
      // series format example (will be provided later by you):
      // [{ name: 'Series A', data: [44, 55, 41] }, { name: 'Series B', data: [53, 32, 33] }]
      series: {
        type: Array,
        default: () => []
      },
      // categories example: ['Jan', 'Feb', 'Mar']
      categories: {
        type: Array,
        default: () => []
      },
      height: {
        type: [String, Number],
        default: 360
      },
      stacked: {
        type: Boolean,
        default: true
      },
      // optional custom colors
      colors: {
        type: Array,
        default: () => []
      },
      // enable/disable data labels
      dataLabels: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        localSeries: JSON.parse(JSON.stringify(this.series || [])),
        chartOptions: {
          chart: {
            type: 'bar',
            stacked: this.stacked,
            toolbar: {
              show: true
            },
            zoom: {
              enabled: true
            }
          },
          plotOptions: {
            bar: {
              horizontal: false,
              borderRadius: 4,
              columnWidth: '60%'
            }
          },
          dataLabels: {
            enabled: this.dataLabels
          },
          stroke: {
            show: true,
            width: 1,
            colors: ['transparent']
          },
          xaxis: {
            categories: this.categories || []
          },
          yaxis: {
            title: {
              text: undefined
            }
          },
          legend: {
            position: 'top',
            horizontalAlign: 'left',
            offsetX: 40
          },
          tooltip: {
            shared: true,
            intersect: false
          },
          fill: {
            opacity: 1
          },
          colors: this.colors.length ? this.colors : undefined,
          responsive: [
            {
              breakpoint: 768,
              options: {
                plotOptions: {
                  bar: {
                    columnWidth: '80%'
                  }
                },
                legend: {
                  position: 'bottom',
                  horizontalAlign: 'center'
                }
              }
            }
          ]
        }
      }
    },
    watch: {
      // update internal series when prop changes
      series: {
        handler (val) {
          this.setSeries(val)
        },
        deep: true
      },
      // update categories when prop changes
      categories (val) {
        this.setCategories(val)
      },
      stacked (val) {
        this.chartOptions.chart.stacked = !!val
        this.updateOptions({ chart: { stacked: !!val } })
      },
      colors (val) {
        if (val && val.length) {
          this.updateOptions({ colors: val })
        }
      },
      dataLabels (val) {
        this.updateOptions({ dataLabels: { enabled: !!val } })
      }
    },
    mounted () {
      // init localSeries & options (in case props provided at mount)
      this.localSeries = JSON.parse(JSON.stringify(this.series || []))
      this.chartOptions.xaxis.categories = this.categories || []
      // ensure chart renders with initial data
      this.$nextTick(() => {
        if (this.$refs.apex && this.$refs.apex.chart) {
          // force update if needed
          try {
            this.$refs.apex.updateOptions({}, false, true)
          } catch (e) {
            // ignore
          }
        }
      })
    },
    methods: {
      /**
       * Set new series (replace)
       * @param {Array} newSeries
       */
      setSeries (newSeries = []) {
        this.localSeries = JSON.parse(JSON.stringify(newSeries || []))
        // update chart via ref if available
        if (this.$refs.apex && this.$refs.apex.updateSeries) {
          this.$refs.apex.updateSeries(this.localSeries, true)
        }
      },
  
      /**
       * Update categories on x-axis
       * @param {Array} newCategories
       */
      setCategories (newCategories = []) {
        this.chartOptions.xaxis.categories = newCategories || []
        this.updateOptions({ xaxis: { categories: this.chartOptions.xaxis.categories } })
      },
  
      /**
       * Utility to update apex options
       * @param {Object} opts - partial options to merge
       * @param {Boolean} redrawPaths
       * @param {Boolean} animate
       */
      updateOptions (opts = {}, redrawPaths = false, animate = true) {
        if (this.$refs.apex && this.$refs.apex.updateOptions) {
          this.$refs.apex.updateOptions(opts, redrawPaths, animate)
        } else {
          // fallback: merge into chartOptions so reactivity may trigger
          this.chartOptions = Object.assign({}, this.chartOptions, opts)
        }
      },
  
      /**
       * Public helper to push a new series (append)
       * @param {Object} seriesObj
       */
      pushSeries (seriesObj) {
        const s = JSON.parse(JSON.stringify(seriesObj))
        this.localSeries = (this.localSeries || []).concat([s])
        if (this.$refs.apex && this.$refs.apex.updateSeries) {
          this.$refs.apex.updateSeries(this.localSeries, true)
        }
      },
  
      /**
       * Clear all series
       */
      clearSeries () {
        this.localSeries = []
        if (this.$refs.apex && this.$refs.apex.updateSeries) {
          this.$refs.apex.updateSeries([], true)
        }
      }
    }
  }
  </script>
  
  <style scoped>
  /* nothing special — styling handled by Apex + Bootstrap (if present) */
  </style>
  


// WEBPACK FOOTER //
// src/views/dashboard/components/grafik.vue